import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { SCRIPT_IMG, ScriptImage } from "@/components/ScriptImage";

const ACCOMM_LINK = (
  <button
    type="button"
    data-testid="faq-accommodation-link"
    onClick={() =>
      window.dispatchEvent(
        new CustomEvent("wedding:navigate", { detail: { id: "accommodation" } })
      )
    }
    className="underline underline-offset-4 decoration-[#76232F]/40 hover:decoration-[#76232F] text-[#76232F] italic"
  >
    Accommodation
  </button>
);

const DRESSCODE_LINK = (
  <button
    type="button"
    data-testid="faq-dresscode-link"
    onClick={() =>
      window.dispatchEvent(
        new CustomEvent("wedding:navigate", { detail: { id: "timeline" } })
      )
    }
    className="underline underline-offset-4 decoration-[#76232F]/40 hover:decoration-[#76232F] text-[#76232F] italic"
  >
    Dress Code
  </button>
);

const FAQS = [
  {
    q: "What do I wear?",
    a: (
      <>
        This is a black tie wedding, so come looking your best! For more
        guidance, please consult the {DRESSCODE_LINK} section of the website.
      </>
    ),
  },
  {
    q: "Will the ceremony be indoors or outdoors?",
    a: "The ceremony is planned outdoors and our reception is situated indoors at The Dairy's Main Venue. If the Queensland weather has other ideas, we have other beautiful undercover options.",
  },
  {
    q: "Can I bring a plus-one?",
    a: "Due to venue and catering limits, we are only able to accommodate guests listed on your invitation. If you have any questions, please reach out to us directly.",
  },
  {
    q: "Are children welcome?",
    a: "If we could have all your babies and children at our wedding, we would. However, we would ask that you celebrate this day with us child-free. We kindly request no children other than immediate family members and those nursing. Please reach out to us personally with any concerns.",
  },
  {
    q: "Where should I stay?",
    a: (
      <>
        If you&rsquo;re looking at staying in Ravensbourne, Toowoomba or
        surrounds, please check the {ACCOMM_LINK} page for further information.
        Complimentary shuttles will be provided which you can request when you
        RSVP.
      </>
    ),
  },
  {
    q: "What if I need to update my RSVP?",
    a: "Simply submit the RSVP form again with your updated details. We'll receive the most recent response.",
  },
  {
    q: "Is there parking at the venue?",
    a: "Yes, there is on-site parking at The Dairy. Please drive carefully as the access road is gravel. Following that, the venue is only a short walk away from the carpark but please be mindful of your steps as it will involve grass and gravel.",
  },
  {
    q: "Can I take photos or videos during the ceremony?",
    a: "We ask you to kindly turn off your phones during the ceremony and refrain from taking photos. We have professional photographers on hand to capture all the special moments and are excited to share their photos with you.",
  },
  {
    q: "Can I BYO?",
    a: "Please note The Dairy is a licensed venue and BYO will not be necessary — we have a bar to take care of all your needs!",
  },
  {
    q: "Who do I contact on the day?",
    a: "Prior to the day, please don't hesitate to reach out to us with any questions. On the day, our wonderful coordinator Madison (0408 930 206) will be available on-site along with the incredible The Dairy team.",
  },
];

function FAQItem({ q, a, isOpen, onToggle, idx }) {
  const paragraphs = Array.isArray(a) ? a : [a];
  return (
    <div className="border-b border-[#76232F]/15">
      <button
        type="button"
        data-testid={`faq-toggle-${idx}`}
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-6 py-4 text-left group"
      >
        <span
          className="italic text-[#76232F] text-[15px] md:text-base leading-snug pr-2"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {q}
        </span>
        <ChevronDown
          className={`shrink-0 text-[#76232F] transition-transform duration-300 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
          size={16}
          strokeWidth={1.5}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="pb-5 space-y-3 text-[15px] md:text-base leading-[1.7] text-[#2C1A1D]/85">
            {paragraphs.map((p, i) => {
              // Block-level React elements (e.g. an illustration wrapped in a
              // div) must not be put inside a <p> — that would be invalid
              // HTML. Render them as-is.
              const isBlockElement =
                p && typeof p === "object" && typeof p.type === "string" &&
                ["div", "img", "figure", "section"].includes(p.type);
              return isBlockElement ? (
                <div key={i}>{p}</div>
              ) : (
                <p key={i}>{p}</p>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FAQPanel() {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <div
      data-testid="panel-block-faqs"
      className="max-w-[680px] mx-auto px-2 md:px-0 pb-16"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      <div className="text-center pt-2">
        <ScriptImage src={SCRIPT_IMG.questions} alt="Questions?" />
      </div>
      <p className="text-center text-[15px] md:text-base leading-[1.8] text-[#2C1A1D]/85 mt-8 mb-10">
        Here are a few things our guests often ask. If there&rsquo;s something
        we haven&rsquo;t covered, please reach out — we&rsquo;d love to hear
        from you.
      </p>

      <div className="border-t border-[#76232F]/15">
        {FAQS.map((faq, idx) => (
          <FAQItem
            key={faq.q}
            idx={idx}
            q={faq.q}
            a={faq.a}
            isOpen={openIdx === idx}
            onToggle={() => setOpenIdx(openIdx === idx ? null : idx)}
          />
        ))}
      </div>
    </div>
  );
}
